import {createSelector} from 'reselect';

import {getTranslations, getListingPage} from './store';

import {getPriceSummaryAmountByType} from '../utils/pricingHelper';
const PRICE_SUMMARY_TYPE_TOTAL = 'TOTAL';

function isString(el) {
    return typeof el === 'string';
}

function findPriceItems(pricingInfo, type, subType) {
    let priceItems = [];

    if (pricingInfo && pricingInfo.items) {
        const pricingInfoItems = pricingInfo.items.filter((item) => {
            if (isString(type) && !isString(subType)) {
                return item.type === type;
            } else if (isString(subType) && !isString(type)) {
                return item.subType === subType;
            } else if (isString(type) && isString(subType)) {
                return item.type === type && item.subType === subType;
            }
            return false;
        });

        for (let quoteItem of pricingInfoItems) {
            let priceItem = {
                label: quoteItem.label,
                amount: quoteItem.amount.localized
            };

            priceItems.push(priceItem);
        }
    }

    return priceItems;
}

const getPriceSummaryDetails = createSelector(
    [getListingPage],
    (listingPage) =>
        listingPage.priceSummary && listingPage.priceSummary.toJS
            ? listingPage.priceSummary.toJS()
            : listingPage.priceSummary || {}
);

const getPriceSummarySecondaryDetails = createSelector(
    [getListingPage],
    (listingPage) =>
        listingPage.priceSummarySecondary && listingPage.priceSummarySecondary.toJS
            ? listingPage.priceSummarySecondary.toJS()
            : listingPage.priceSummarySecondary || {}
);

const getPriceLabelDetails = createSelector(
    [getListingPage],
    (listingPage) =>
        listingPage.priceLabel && listingPage.priceLabel.toJS
            ? listingPage.priceLabel.toJS()
            : listingPage.priceLabel || {}
);

// This is the priceDetails from the graphql-traveler-checkout partials.
// This note is important until we remove all the PDS related details.
const getPriceDetails = createSelector(
    [getListingPage],
    (listingPage) => {
        // We store the object returned from graphql into priceDetails.
        // It defaults to a POO, which doesn't have toJS.
        // This object contains errors, priceDetails and endTime.
        const priceDetails =
            listingPage.priceDetails && listingPage.priceDetails.toJS
                ? listingPage.priceDetails.toJS()
                : listingPage.priceDetails || {};
        // Return just the priceDetails data.
        // This is used to determine avg nightly, total, and is the chunk of
        // data passed directly to the PriceDetails component.
        return priceDetails.priceDetails || {};
    }
);

const getAverageNightlyAmountByType = (priceDetails = {averageNightlies: []}, type) => {
    return (
        priceDetails.averageNightlies.find((currentTotal) => currentTotal.type === type) || {
            perNightCost: {localized: ''}
        }
    ).perNightCost.localized;
};

const getAverageNightly = createSelector(
    [getPriceDetails],
    (priceDetails) => {
        let averageNightlyBase = '';
        let averageNightlyTotalRent = '';
        let averageNightlyTotalTrip = '';

        if (priceDetails.averageNightlies) {
            averageNightlyBase = getAverageNightlyAmountByType(priceDetails, 'BASE_RENT');
            averageNightlyTotalRent = getAverageNightlyAmountByType(priceDetails, 'TOTAL_RENT');
            averageNightlyTotalTrip = getAverageNightlyAmountByType(priceDetails, 'TOTAL_TRIP');
        }

        return {
            averageNightlyBase,
            averageNightlyTotalRent,
            averageNightlyTotalTrip
        };
    }
);

const getTotals = createSelector(
    [getPriceDetails, getPriceSummaryDetails, getPriceSummarySecondaryDetails],
    (priceDetails, priceSummary, priceSummarySecondary) => {
        const defaultValue =
            priceDetails.totals && priceDetails.totals[0] ? priceDetails.totals[0].amount : '';
        const totalValue = getPriceSummaryAmountByType(
            priceSummary,
            priceSummarySecondary,
            PRICE_SUMMARY_TYPE_TOTAL
        );
        return {
            totalWithoutDeposits: totalValue || defaultValue,
            totalWithDeposits: '',
            totalInCents: priceDetails.totalNumeric && priceDetails.totalNumeric.totalInCents,
            isNonPCListing: Boolean(!totalValue)
        };
    }
);

const getFirstPayment = createSelector(
    [getPriceDetails, getTranslations],
    (priceDetails, trans) => {
        if (!priceDetails.dueNow || priceDetails.dueNow.length < 2) {
            return null;
        }

        const dueNow = priceDetails.dueNow.find((dn) => dn.amount);
        const amount = dueNow && dueNow.amount;

        if (!amount) {
            return null;
        }

        return {
            amount,
            label: trans.quoteFirstPayment,
            tooltipContent: trans.payTheRestPopoverManager
        };
    }
);

const getConditionalInstantBook = createSelector(
    [getPriceDetails],
    (priceDetails) => !!priceDetails.instantBooking
);

const getPriceLabels = createSelector(
    [getTranslations, getPriceLabelDetails],
    (trans, priceLabel) => {
        const taxesAndFees =
            priceLabel && priceLabel.pricePeriodDescription
                ? priceLabel.pricePeriodDescription
                : trans.taxesAndFees;

        return {
            taxesAndFees,
            total: trans.total,
            viewDetails: trans.viewDetails
        };
    }
);

export {
    findPriceItems,
    getAverageNightly,
    getConditionalInstantBook,
    getPriceDetails,
    getPriceLabels,
    getPriceLabelDetails,
    getPriceSummaryDetails,
    getPriceSummarySecondaryDetails,
    getTotals,
    getFirstPayment,
    PRICE_SUMMARY_TYPE_TOTAL
};
